import { useState, useEffect } from 'react'
import { AlarmClock, Calendar, RefreshCw, Loader2, ChevronLeft, ChevronRight } from 'lucide-react'
import { rrGetAdminAttendance } from '@/actions/rr'

interface AgentRow {
  user_id: number; name: string
  status: 'present' | 'absent'
  punch_in: string | null; punch_out: string | null
  total_hours: number | null; total_hours_fmt: string
  break_minutes: number; on_break: boolean
}

const AV_COLORS = ['#2f9e6b','#3b82c4','#b9772e','#8a5cd1','#c2557a','#2aa1a8']
function avColor(n: string) { let h = 0; for (const c of n) h = (h * 31 + c.charCodeAt(0)) % AV_COLORS.length; return AV_COLORS[h] }
function initials(n: string) { const p = n.trim().split(' '); return ((p[0]?.[0]??'')+(p[1]?.[0]??'')).toUpperCase() }

function StatusPill({ status, onBreak }: { status: string; onBreak: boolean }) {
  if (status === 'present' && onBreak)
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-amber-500" />On Break</span>
  if (status === 'present')
    return <span className="inline-flex items-center gap-1 text-xs font-bold text-green-700 bg-green-50 px-2 py-0.5 rounded-full"><span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />Present</span>
  return <span className="text-xs font-bold text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Absent</span>
}

function dateToISO(d: Date) {
  return d.toISOString().slice(0, 10)
}

function offsetDate(iso: string, days: number): string {
  const d = new Date(iso)
  d.setDate(d.getDate() + days)
  return dateToISO(d)
}

function formatDisplay(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
}

export default function AttendancePage() {
  const [dateStr, setDateStr] = useState(dateToISO(new Date()))
  const [agents, setAgents] = useState<AgentRow[]>([])
  const [summary, setSummary] = useState({ present: 0, absent: 0, date: '' })
  const [loading, setLoading] = useState(true)

  function load(d: string) {
    setLoading(true)
    rrGetAdminAttendance(d).then(res => {
      const d2 = res?.data as { agents?: AgentRow[]; present_count?: number; absent_count?: number; date?: string } ?? {}
      setAgents((d2.agents ?? []) as AgentRow[])
      setSummary({
        present: d2.present_count ?? 0,
        absent: d2.absent_count ?? 0,
        date: d2.date ?? '',
      })
    }).finally(() => setLoading(false))
  }

  useEffect(() => { load(dateStr) }, [dateStr])

  const isToday = dateStr === dateToISO(new Date())

  return (
    <div className="h-full min-h-0 flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brandColor flex items-center justify-center flex-shrink-0">
            <AlarmClock className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">Attendance</h1>
            <p className="text-sm text-gray-500 mt-0.5">Daily agent punch-in / punch-out log.</p>
          </div>
        </div>
        <button onClick={() => load(dateStr)} className="inline-flex items-center gap-2 h-9 px-3 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {/* Date nav */}
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => setDateStr(offsetDate(dateStr, -1))} className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-50">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex items-center gap-2 h-9 border border-gray-300 rounded-lg px-3 bg-white">
          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <input
            type="date"
            value={dateStr}
            max={dateToISO(new Date())}
            onChange={e => setDateStr(e.target.value)}
            className="text-sm font-semibold text-gray-900 border-0 outline-none bg-transparent"
          />
        </div>
        <button
          onClick={() => setDateStr(offsetDate(dateStr, 1))}
          disabled={isToday}
          className="w-9 h-9 border border-gray-300 rounded-lg flex items-center justify-center text-gray-600 hover:bg-gray-50 disabled:opacity-30"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="text-sm text-gray-500">{formatDisplay(dateStr)}</span>
        {isToday && <span className="text-xs font-bold text-brandColor bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">Today</span>}
      </div>

      {/* Summary strip */}
      {!loading && (
        <div className="flex gap-3 mb-4">
          {[
            { label: 'Present', value: summary.present, cls: 'bg-green-50 border-green-200 text-green-700' },
            { label: 'Absent',  value: summary.absent,  cls: 'bg-gray-50  border-gray-200  text-gray-500'  },
            { label: 'Total',   value: agents.length,   cls: 'bg-white    border-gray-200  text-gray-700'  },
          ].map(s => (
            <div key={s.label} className={`flex items-center gap-2.5 px-4 py-2.5 border rounded-xl ${s.cls}`}>
              <span className="text-2xl font-extrabold">{s.value}</span>
              <span className="text-xs font-bold uppercase tracking-wider opacity-70">{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {/* Table — flex-1 + min-h-0 so it fills remaining height and scrolls */}
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden flex-1 min-h-0 flex flex-col">
        <div className="grid grid-cols-[1fr_110px_100px_100px_100px_80px] gap-0 px-5 py-3 text-[10px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100 bg-gray-50 flex-shrink-0">
          <span>Agent</span><span>Status</span><span>Punch In</span><span>Punch Out</span><span>Hours</span><span>Break</span>
        </div>

        {loading
          ? <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin text-gray-300" /></div>
          : agents.length === 0
            ? <div className="flex justify-center items-center h-32 text-gray-400 text-sm">No agents found</div>
            : <div className="divide-y divide-gray-50 overflow-y-auto flex-1">
              {[...agents]
                .sort((a, b) => {
                  // present (incl. on_break) first, then absent; alpha within each group
                  const aScore = a.status === 'present' ? 0 : 1
                  const bScore = b.status === 'present' ? 0 : 1
                  if (aScore !== bScore) return aScore - bScore
                  return a.name.localeCompare(b.name)
                })
                .map(a => (
                <div key={a.user_id} className={`grid grid-cols-[1fr_110px_100px_100px_100px_80px] gap-0 px-5 py-3 items-center ${a.status === 'absent' ? 'opacity-50' : ''}`}>
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0" style={{ background: avColor(a.name) }}>{initials(a.name)}</span>
                    <span className="text-sm font-semibold text-gray-900 truncate">{a.name}</span>
                  </div>
                  <div><StatusPill status={a.status} onBreak={a.on_break} /></div>
                  <div className="text-sm text-gray-700 font-medium">{a.punch_in ?? '—'}</div>
                  <div className="text-sm text-gray-700 font-medium">{a.punch_out ?? '—'}</div>
                  <div className="text-sm font-bold text-gray-900">{a.total_hours_fmt}</div>
                  <div className="text-xs text-gray-500">{a.break_minutes > 0 ? `${a.break_minutes}m` : '—'}</div>
                </div>
              ))}
            </div>
        }
      </div>
    </div>
  )
}
